import { useText } from "@/lib/content";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { validateFields, applySpinRules, spinsForProvince } from "@/lib/validators";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { ConfigField } from "@/components/form/ConfigField";
import { ErrorSummary, Notice } from "@/components/form/FormFeedback";
import { Loading } from "@/components/layout/RequireAuth";
import { usePageTitle } from "@/components/layout/AppLayout";

export function useRegistrationConfig() {
    const [config, setConfig] = useState(null);
    const [error, setError] = useState("");
    useEffect(() => {
        api.get("/meta/registration-config").then(({ data }) => setConfig(data)).catch((e) => setError(errorMessage(e)));
    }, []);
    return { config, error };
}

export function spinHintFor(spins, provincia) {
    const matches = spinsForProvince(spins, provincia);
    return { matches, none: Boolean(provincia) && matches.length === 0 };
}

const focusField = (key) => setTimeout(() => document.getElementById(`field-${key}`)?.focus(), 50);

export default function Register() {
    const t = useText();
    usePageTitle("Registra la tua azienda");
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const { config, error: loadError } = useRegistrationConfig();
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");
    const [busy, setBusy] = useState(false);

    const sections = useMemo(() => {
        const out = [];
        (config?.fields || []).forEach((f) => {
            let s = out.find((x) => x.name === f.section);
            if (!s) out.push((s = { name: f.section, fields: [] }));
            s.fields.push(f);
        });
        return out;
    }, [config]);

    if (user) return <Navigate to={homeForRole(user.role)} replace />;
    if (loadError) return <div className="mx-auto max-w-3xl px-4 py-12"><Notice kind="error" role="alert">{loadError}</Notice></div>;
    if (!config) return <Loading />;

    const onChange = (key, value) => {
        setValues((v) => {
            const next = { ...v, [key]: value };
            if (key === "provincia") next.spin_id = applySpinRules(config.spins, value);
            return next;
        });
        if (errors[key]) setErrors(({ [key]: _omit, ...rest }) => rest);
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        const clientErrors = validateFields(config.fields, values);
        if (Object.keys(clientErrors).length) {
            setErrors(clientErrors);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/register", { values });
            setUser(data.user);
            navigate("/profilo", { replace: true, state: { registered: true } });
        } catch (err) {
            const fe = fieldErrors(err);
            if (fe) setErrors(fe);
            else setSubmitError(errorMessage(err, "Registrazione non riuscita, riprova"));
        } finally {
            setBusy(false);
        }
    };

    const spinHint = spinHintFor(config.spins, values.provincia);

    return (
        <div className="edi-plainbg edi-fill" data-testid="register-dotgrid">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
            <p className="edi-eyebrow">Area aziende</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold" data-testid="register-title">{t("auth.register_title")}</h1>
            <p className="mt-3 text-edi-muted whitespace-pre-line">{t("auth.register_intro")}</p>
            <form noValidate onSubmit={onSubmit} className="mt-8 space-y-8" data-testid="register-form">
                <ErrorSummary errors={errors} fields={config.fields} />
                {submitError && <Notice kind="error" role="alert" testId="register-submit-error">{submitError}</Notice>}
                {sections.map((s) => (
                    <fieldset key={s.name} className="edi-card p-5 sm:p-6">
                        <legend className="font-heading text-lg font-bold px-2">{s.name}</legend>
                        <div className="grid gap-5 sm:grid-cols-2">
                            {s.fields.map((f) => (
                                <div key={f.key} className={["checkbox", "spin"].includes(f.type) || f.key === "ragione_sociale" ? "sm:col-span-2" : ""}>
                                    <ConfigField field={f} value={values[f.key]} email={values.email} error={errors[f.key]} onChange={onChange} provinces={config.provinces} spins={config.spins} spinHint={spinHint} />
                                </div>
                            ))}
                        </div>
                    </fieldset>
                ))}
                <div className="flex flex-wrap items-center gap-4">
                    <button type="submit" className="btn-primary" disabled={busy} data-testid="register-submit-button">
                        {busy ? "Registrazione in corso…" : "Registra la tua azienda"}
                    </button>
                    <p>Hai già un account? <Link to="/accedi" data-testid="register-login-link">Accedi</Link></p>
                </div>
            </form>
        </div>
        </div>
    );
}

export { focusField };
